"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  courtLines,
  courtQuad,
  courtStripes,
  createCourtProjector,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type PadelCourtPreviewProps = {
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourSets: number; opponentSets: number };
  sets?: Array<{ yourScore: number; opponentScore: number }>;
};

// ─── Court geometry (blue turf inside the glass box) ────────────────────────
// 20m × 10m: service lines 6.95m off the net, a centre service line between
// them. The far wall is glass at both ends with mesh between; the two end
// walls are glass planes rising from the baselines toward the camera.

const P = createCourtProjector({
  yFar: 66,
  yNear: 204,
  farHalfWidth: 120,
  nearHalfWidth: 144,
});

const SERVICE = 0.1525;
/** Screen height of the 4m glass at the far wall; nearer = taller. */
const WALL = 36;

const [FAR_L_X, FAR_Y] = P(0, 0);
const [FAR_R_X] = P(1, 0);
const [NEAR_L_X, NEAR_Y] = P(0, 1);
const [NEAR_R_X] = P(1, 1);
const NEAR_WALL = WALL * ((NEAR_R_X - NEAR_L_X) / (FAR_R_X - FAR_L_X));
const GLASS_END = 0.2;
const [GLASS_L_X] = P(GLASS_END, 0);
const [GLASS_R_X] = P(1 - GLASS_END, 0);
const MESH_TOP = FAR_Y - WALL * 0.75;

const TURF = courtQuad(P, 0, 0, 1, 1);
const TURF_STRIPES = courtStripes(P, 10, 0, 1, 0, 1);
const LINES = courtLines(P, [
  [SERVICE, 0, SERVICE, 1],
  [1 - SERVICE, 0, 1 - SERVICE, 1],
  [SERVICE, 0.5, 1 - SERVICE, 0.5],
]);
const WALL_BASE = courtLines(P, [
  [0, 1, 0, 0],
  [0, 0, 1, 0],
  [1, 0, 1, 1],
]);
const NET_SHADOW = courtQuad(P, 0.5, 0, 0.522, 1);

const LEFT_END_GLASS = `M${FAR_L_X},${FAR_Y}L${FAR_L_X},${FAR_Y - WALL}L${NEAR_L_X},${NEAR_Y - NEAR_WALL}L${NEAR_L_X},${NEAR_Y}Z`;
const RIGHT_END_GLASS = `M${FAR_R_X},${FAR_Y}L${FAR_R_X},${FAR_Y - WALL}L${NEAR_R_X},${NEAR_Y - NEAR_WALL}L${NEAR_R_X},${NEAR_Y}Z`;
const GLASS_FRAME = [
  `M${FAR_L_X},${FAR_Y - WALL}L${NEAR_L_X},${NEAR_Y - NEAR_WALL}`,
  `M${FAR_R_X},${FAR_Y - WALL}L${NEAR_R_X},${NEAR_Y - NEAR_WALL}`,
  `M${FAR_L_X},${FAR_Y}L${FAR_L_X},${FAR_Y - WALL}`,
  `M${FAR_R_X},${FAR_Y}L${FAR_R_X},${FAR_Y - WALL}`,
  `M${GLASS_L_X},${FAR_Y}L${GLASS_L_X},${FAR_Y - WALL}`,
  `M${GLASS_R_X},${FAR_Y}L${GLASS_R_X},${FAR_Y - WALL}`,
  `M${FAR_L_X},${FAR_Y - WALL}L${GLASS_L_X},${FAR_Y - WALL}`,
  `M${GLASS_R_X},${FAR_Y - WALL}L${FAR_R_X},${FAR_Y - WALL}`,
  `M${GLASS_L_X},${MESH_TOP}L${GLASS_R_X},${MESH_TOP}`,
].join("");
// End-wall panel joints at a third and two-thirds of the depth.
const PANEL_JOINTS = [1 / 3, 2 / 3]
  .flatMap((v) => {
    const [lx, ly] = P(0, v);
    const [rx] = P(1, v);
    const h = WALL * ((rx - lx) / (FAR_R_X - FAR_L_X));
    return [`M${lx},${ly}L${lx},${ly - h}`, `M${rx},${ly}L${rx},${ly - h}`];
  })
  .join("");

function PadelCourtArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#030916" />
            <stop offset="1" stopColor="#0c2442" />
          </linearGradient>
          <linearGradient id={`${id}-turf`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2f7fd4" />
            <stop offset="0.55" stopColor="#1f63b3" />
            <stop offset="1" stopColor="#134687" />
          </linearGradient>
          <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="rgba(200,230,255,0.16)" />
            <stop offset="0.45" stopColor="rgba(200,230,255,0.05)" />
            <stop offset="0.55" stopColor="rgba(255,255,255,0.16)" />
            <stop offset="0.62" stopColor="rgba(200,230,255,0.04)" />
            <stop offset="1" stopColor="rgba(200,230,255,0.1)" />
          </linearGradient>
          <pattern
            id={`${id}-mesh`}
            width="3"
            height="3"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0,0L3,3M3,0L0,3"
              stroke="rgba(170,200,230,0.35)"
              strokeWidth="0.35"
            />
          </pattern>
          <pattern
            id={`${id}-turf-grain`}
            width="4"
            height="3"
            patternUnits="userSpaceOnUse"
          >
            <rect x="0.5" y="0.4" width="0.5" height="1.4" fill="#ffffff" />
            <rect x="2.6" y="1.6" width="0.5" height="1.2" fill="#000000" />
          </pattern>
        </defs>

        {/* Night sky over the box, floodlight heads on the far corners. */}
        <rect width="300" height="220" fill={`url(#${id}-sky)`} />
        <rect x="0" y={FAR_Y} width="300" height={220 - FAR_Y} fill="#0b1d36" />

        {/* Far wall: glass panels at the ends, mesh fence between. */}
        <rect
          x={GLASS_L_X}
          y={MESH_TOP}
          width={GLASS_R_X - GLASS_L_X}
          height={FAR_Y - MESH_TOP}
          fill={`url(#${id}-mesh)`}
        />
        <rect
          x={FAR_L_X}
          y={FAR_Y - WALL}
          width={GLASS_L_X - FAR_L_X}
          height={WALL}
          fill={`url(#${id}-glass)`}
        />
        <rect
          x={GLASS_R_X}
          y={FAR_Y - WALL}
          width={FAR_R_X - GLASS_R_X}
          height={WALL}
          fill={`url(#${id}-glass)`}
        />

        {/* Blue turf with sand-filled grain and mowing panels. */}
        <path d={TURF} fill={`url(#${id}-turf)`} />
        <path d={TURF} fill={`url(#${id}-turf-grain)`} opacity="0.07" />
        <path d={TURF_STRIPES} fill="rgba(255,255,255,0.035)" />
        <path
          d={LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.92)"
          strokeWidth="1.6"
        />
        <path
          d={WALL_BASE}
          {...PAINT_LINE}
          stroke="rgba(4,14,30,0.6)"
          strokeWidth="2"
        />

        {/* Net. */}
        <path d={NET_SHADOW} fill="rgba(3,14,34,0.25)" />
        <rect
          x="148.4"
          y={FAR_Y - 6}
          width="3.2"
          height={NEAR_Y - FAR_Y - 6}
          fill="rgba(12,20,34,0.7)"
        />
        <rect
          x="147.8"
          y={FAR_Y - 6}
          width="1.3"
          height={NEAR_Y - FAR_Y - 6}
          fill="#f1f5fa"
        />
        <rect
          x="147"
          y={FAR_Y - 9}
          width="6"
          height="9"
          rx="1"
          fill="#1a2433"
        />
        <rect
          x="145.6"
          y={NEAR_Y - 17}
          width="8.8"
          height="16"
          rx="1.4"
          fill="#1a2433"
        />
        <rect
          x="145.6"
          y={NEAR_Y - 17}
          width="2.4"
          height="16"
          rx="1"
          fill="rgba(255,255,255,0.2)"
        />

        {/* End walls: glass planes rising from the baselines. */}
        <path d={LEFT_END_GLASS} fill={`url(#${id}-glass)`} />
        <path d={RIGHT_END_GLASS} fill={`url(#${id}-glass)`} />
        <path
          d={GLASS_FRAME + PANEL_JOINTS}
          {...PAINT_LINE}
          stroke="rgba(14,24,40,0.85)"
          strokeWidth="1.6"
        />
        <path
          d={GLASS_FRAME}
          {...PAINT_LINE}
          stroke="rgba(190,215,240,0.25)"
          strokeWidth="0.6"
        />
      </CourtSvg>

      {/* Lighting: LED floods over the far corners, a cool wash on the turf. */}
      <div className="absolute inset-x-0 top-0 h-[90px] bg-[radial-gradient(ellipse_22%_60%_at_12%_8%,rgba(225,240,255,0.5),transparent_70%),radial-gradient(ellipse_22%_60%_at_88%_8%,rgba(225,240,255,0.5),transparent_70%)]" />
      <div className="absolute top-[14px] left-[9%] h-[3px] w-[16px] rounded-full bg-white shadow-[0_0_10px_3px_rgba(220,238,255,0.8)]" />
      <div className="absolute top-[14px] right-[9%] h-[3px] w-[16px] rounded-full bg-white shadow-[0_0_10px_3px_rgba(220,238,255,0.8)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_50%_55%,rgba(150,205,255,0.16),transparent_72%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,rgba(2,8,20,0.4)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.42)_100%)]" />
    </>
  );
}

const PadelBall = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "absolute overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_30%,#fbffb0_0%,#e8ff3c_38%,#b3cc00_100%)] shadow-[inset_-1.5px_-2px_3px_rgba(0,0,0,0.3),0_0_10px_rgba(230,255,60,0.35)]",
      className,
    )}
  >
    <div className="absolute top-[-20%] bottom-[-20%] left-[-62%] w-[85%] rounded-[100%] border-[1.5px] border-white/80" />
    <div className="absolute top-[-20%] right-[-62%] bottom-[-20%] w-[85%] rounded-[100%] border-[1.5px] border-white/80" />
  </div>
);

export function PadelCourtPreview({
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  sets,
}: PadelCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourTeamWon = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourSets > 0 ||
      matchScore.opponentSets > 0 ||
      (sets?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Padel"
      frameClassName="bg-[#0b1d36]"
      matchState={matchState}
      scene={<PadelCourtArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[86%] left-[33%] h-[6px] w-[18px]" />
            <PadelBall className="top-[81%] left-[34%] h-[12px] w-[12px] rotate-[20deg]" />
          </>
        ) : (
          <>
            {/* Ball off the back glass, its shadow on the turf. */}
            <PropShadow className="top-[69%] left-[62%] h-[6px] w-[20px]" />
            <div className="absolute top-[47%] left-[64%] h-[5px] w-[36px] rotate-[24deg] rounded-full bg-[linear-gradient(90deg,rgba(240,255,140,0.45),transparent)]" />
            <PadelBall className="top-[42%] left-[61%] h-[14px] w-[14px] rotate-[18deg]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourTeamWon}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourSets}
            right={matchScore.opponentSets}
            leftWinner={yourTeamWon}
            rightWinner={opponentWon}
            breakdown={sets}
            accentClassName="bg-[#e8ff3c] shadow-[0_0_12px_rgba(232,255,60,0.7)]"
          />
        ) : null
      }
    />
  );
}
